// Loaders for the "related ticket" lookups. They fetch tasks and incidents the
// same way TaskView / IncidentView do (light endpoints, batches, IndexedDB
// cache) and share those views' caches, so the lookup opens instantly when the
// view has been visited.
import localforage from "localforage";
import config from "../config";
import { authFetch } from "./Security/authFetch";
import { formatDuration } from "./durationUtils";
import { TaskData } from "./Task/taskTypes";
import { IncidentData } from "./Incident/incidentTypes";

type Page<T> = { content: T[]; totalPages: number };
type Cache<T> = { rows: T[]; ts: number } | null;

// rows is always the full list loaded so far (replace, don't append)
type OnRows<T> = (rows: T[]) => void;

// Grid updates are throttled: every update makes AG Grid re-sort and re-filter
const COMMIT_INTERVAL_MS = 1000;

const fetchJson = async (url: string) => {
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`Request failed: ${url}`);
  return res.json();
};

const session = () => {
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  return {
    userId: localStorage.getItem("userid"),
    isAdmin,
    isForPetrobras: localStorage.getItem("isForPetrobras") === "true",
    isForTelebras: localStorage.getItem("isForTelebras") === "true",
    isForNilesat: localStorage.getItem("isForNilesat") === "true",
  };
};

// Petrobras users (admin or not) only relate Petrobras tickets
const onlyPetrobras = <T,>(rows: T[]): T[] =>
  session().isForPetrobras
    ? rows.filter((r: any) =>
        String(r.affectedCustomer ?? "").includes("Petrobras"),
      )
    : rows;

// -------------------- Tasks (mirrors TaskView) --------------------
export const loadLookupTasks = async (
  onRows: OnRows<TaskData>,
  isCancelled: () => boolean,
): Promise<void> => {
  const { userId, isAdmin, isForPetrobras } = session();
  // Same key and row format as TaskView, so both read each other's cache
  const CACHE_KEY = `tasks_v2_${
    isAdmin ? (isForPetrobras ? "admin_petrobras" : "admin") : userId
  }`;

  let showingCache = false;
  try {
    const cached = await localforage.getItem<Cache<TaskData>>(CACHE_KEY);
    if (!isCancelled() && cached?.rows?.length) {
      onRows(onlyPetrobras(cached.rows));
      showingCache = true;
    }
  } catch (e) {
    console.error("Failed to read task cache", e);
  }

  const formatBatch = (batch: TaskData[]): TaskData[] =>
    (isAdmin && isForPetrobras ? onlyPetrobras(batch) : batch).map(
      (item: any) => ({
        ...item,
        taskResolutionTime: formatDuration(item.taskResolutionTime),
      }),
    );

  const fresh: TaskData[] = [];
  let lastCommit = 0;
  const commit = () => {
    if (isCancelled()) return;
    lastCommit = Date.now();
    onRows(onlyPetrobras([...fresh]));
  };
  // Without a cache rows stream in; with one, the fresh list replaces it at the end
  const onBatch = (batch: TaskData[]) => {
    if (isCancelled()) return;
    fresh.push(...formatBatch(batch));
    if (!showingCache && Date.now() - lastCommit >= COMMIT_INTERVAL_MS) commit();
  };

  if (isAdmin) {
    // /list returns light rows (no journal/communication/attachments);
    // pages are fetched in parallel and handed over in order (newest first)
    const BATCH_SIZE = 1000;
    const CONCURRENCY = 4;
    const customerParam = isForPetrobras ? "&customer=Petrobras" : "";
    const pageUrl = (page: number) =>
      `${config.apiBaseUrl}/api/tasks/list?offset=${page * BATCH_SIZE}&limit=${BATCH_SIZE}${customerParam}`;

    const first: Page<TaskData> = await fetchJson(pageUrl(0));
    onBatch(first.content ?? []);

    const totalPages = first.totalPages ?? 1;
    const results: (TaskData[] | undefined)[] = [];
    let nextPage = 1;
    let nextToFlush = 1;
    const flush = () => {
      while (results[nextToFlush]) {
        onBatch(results[nextToFlush]!);
        results[nextToFlush] = [];
        nextToFlush++;
      }
    };
    const worker = async () => {
      while (!isCancelled() && nextPage < totalPages) {
        const page = nextPage++;
        const data: Page<TaskData> = await fetchJson(pageUrl(page));
        results[page] = data.content ?? [];
        flush();
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  } else {
    const data = await fetchJson(`${config.apiBaseUrl}/api/tasks/user/${userId}`);
    onBatch(Array.isArray(data) ? data : data?.content ?? []);
  }

  if (isCancelled()) return;
  commit();
  localforage
    .setItem(CACHE_KEY, { rows: fresh, ts: Date.now() })
    .catch((e) => console.error("Failed to write task cache", e));
};

// -------------------- Incidents (mirrors IncidentView) --------------------
export const loadLookupIncidents = async (
  onRows: OnRows<IncidentData>,
  isCancelled: () => boolean,
): Promise<void> => {
  const { userId, isAdmin, isForPetrobras, isForTelebras, isForNilesat } =
    session();
  const CACHE_KEY = `incidents_${isAdmin ? "admin" : userId}`;

  // Like IncidentView: a cached list is used as-is, the API is only hit without one
  try {
    const cached = await localforage.getItem<Cache<IncidentData>>(CACHE_KEY);
    if (cached?.rows?.length) {
      if (!isCancelled()) onRows(onlyPetrobras(cached.rows));
      return;
    }
  } catch (e) {
    console.error("Failed to read incident cache", e);
  }

  const BATCH_SIZE = 2000;
  const pageUrl = (offset: number) =>
    isAdmin && (isForTelebras || isForPetrobras || isForNilesat)
      ? `${config.apiBaseUrl}/api/incidents/telebras/getAllIncidents/${userId}?offset=${offset}&limit=${BATCH_SIZE}`
      : isAdmin
        ? `${config.apiBaseUrl}/api/incidents/getAllIncidents?offset=${offset}&limit=${BATCH_SIZE}`
        : `${config.apiBaseUrl}/api/incidents/user/${userId}/getAllIncidents?offset=${offset}&limit=${BATCH_SIZE}`;

  const userMap = new Map<string, string>();
  const allRows: IncidentData[] = [];
  let lastCommit = 0;
  let offset = 0;
  let hasMore = true;

  while (hasMore && !isCancelled()) {
    const data: Page<IncidentData> = await fetchJson(pageUrl(offset));
    const batch = data.content ?? [];
    if (batch.length < BATCH_SIZE) hasMore = false;

    // Resolve creator usernames once per user, as IncidentView does
    const newUserIds = Array.from(
      new Set(batch.map((i) => i.userid).filter((id) => !userMap.has(id))),
    );
    await Promise.all(
      newUserIds.map(async (id) => {
        if (id == null) return;
        try {
          const user = await fetchJson(`${config.apiBaseUrl}/api/users/${id}`);
          userMap.set(id, user.username || "");
        } catch {
          userMap.set(id, "");
        }
      }),
    );

    allRows.push(
      ...batch.map((item) => ({
        ...item,
        responseTimeToOpen: formatDuration(item.responseTimeToOpen),
        responseTimeToClose: formatDuration(item.responseTimeToClose),
        incidentResolutionTime: formatDuration(item.incidentResolutionTime),
        user: userMap.get(item.userid) || "",
      })),
    );
    if (Date.now() - lastCommit >= COMMIT_INTERVAL_MS || !hasMore) {
      lastCommit = Date.now();
      if (!isCancelled()) onRows(onlyPetrobras([...allRows]));
    }
    offset += BATCH_SIZE;
  }

  if (isCancelled()) return;
  localforage
    .setItem(CACHE_KEY, { rows: allRows, ts: Date.now() })
    .catch((e) => console.error("Failed to write incident cache", e));
};
